
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getCsrfHeaders } from '@/lib/csrf';
import type { 
    WorkflowRunSummary, 
    CheckpointData, 
    LogEntry, 
    ReplayState,
    ListRunsResponse,
    GetCheckpointsResponse,
    GetLogsResponse,
    GetChildRunsResponse,
    ChildWorkflowRunSummary,
    AggregatedCheckpointData,
    CheckpointLookupResult,
} from '@/types/workflow-replay';

/**
 * Make an authenticated API request using fetch.
 * Uses same-origin credentials and CSRF headers for Laravel session auth.
 */
async function apiGet<T>(url: string): Promise<T> {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...getCsrfHeaders(),
        },
        credentials: 'same-origin',
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || `Request failed with status ${response.status}`);
    }

    return response.json();
}

interface UseWorkflowReplayOptions {
    /** The workflow's unique ID (UUID) */
    workflowId: string;
    /** The workflow's key (e.g., "workflow.instrument.ingest") */
    workflowKey: string;
    /** Whether replay mode is active (controlled by parent/global context) */
    isReplayMode: boolean;
    /** Optional: Pre-select a specific run ID (for navigating into child workflows) */
    initialRunId?: string | null;
    onNodeSelected?: (stateName: string, checkpoint: CheckpointData | null, aggregated: AggregatedCheckpointData | null) => void;
}

interface UseWorkflowReplayReturn extends Omit<ReplayState, 'isReplayMode'> {
    /** Whether replay mode is active (passed through from options) */
    isReplayMode: boolean;
    availableRuns: WorkflowRunSummary[];
    selectRun: (runId: string) => Promise<void>;
    selectNode: (nodeId: string) => void;
    /** Get checkpoint for a node - returns both single checkpoint and aggregated map iteration data */
    getCheckpointForNode: (
        stateName: string, 
        mapNodeId?: string,
        parallelInfo?: { parallelNodeId: string; branchIndex: number }
    ) => CheckpointLookupResult;
    getLogsForNode: (stateName: string) => LogEntry[];
    /** Get aggregated checkpoint data for a node inside a map iterator */
    getMapIterationCheckpoints: (mapNodeId: string, innerStateName: string) => AggregatedCheckpointData | null;
    refreshRuns: () => Promise<void>;
    /** Get child workflow runs for a given state (step) in the current run */
    getChildRunsForStep: (stepId: string) => Promise<ChildWorkflowRunSummary[]>;
    /** Check if a child run exists for a given state name */
    hasChildRun: (stateName: string) => boolean;
    /** Cached child runs indexed by parent_state_name */
    childRunsByState: Map<string, ChildWorkflowRunSummary>;
    /** Clear replay data (called when exiting replay mode) */
    clearReplayData: () => void;
}

/**
 * Parse a checkpoint state_name to extract map iteration info.
 * Format: "workflow.key:map:MapNodeId:IterationIndex:InnerStateName"
 * 
 * @returns null if not a map iteration checkpoint, otherwise parsed info
 */
function parseMapIterationStateName(stateName: string): {
    workflowKey: string;
    mapNodeId: string;
    iterationIndex: number;
    innerStateName: string;
} | null {
    // Match pattern: anything:map:NodeId:Index:StateName
    const match = stateName.match(/^(.+):map:([^:]+):(\d+):(.+)$/);
    if (!match) {
        return null;
    }
    return {
        workflowKey: match[1],
        mapNodeId: match[2],
        iterationIndex: parseInt(match[3], 10),
        innerStateName: match[4],
    };
}

/**
 * Parse a checkpoint state_name to extract parallel branch info.
 * Format: "{workflowId}:branch:{parallelNodeId}:{branchIndex}:{innerStateName}"
 * 
 * @returns null if not a parallel branch checkpoint, otherwise parsed info
 */
function parseParallelBranchStateName(stateName: string): {
    workflowId: string;
    parallelNodeId: string;
    branchIndex: number;
    innerStateName: string;
} | null {
    // Match pattern: anything:branch:NodeId:Index:StateName
    const match = stateName.match(/^(.+):branch:([^:]+):(\d+):(.+)$/);
    if (!match) {
        return null;
    }
    return {
        workflowId: match[1],
        parallelNodeId: match[2],
        branchIndex: parseInt(match[3], 10),
        innerStateName: match[4],
    };
}

/**
 * Build aggregated checkpoint data from individual iteration checkpoints.
 */
function buildAggregatedCheckpoint(
    mapNodeId: string,
    innerStateName: string,
    iterations: Map<number, CheckpointData>
): AggregatedCheckpointData {
    let completedCount = 0;
    let failedCount = 0;
    let runningCount = 0;
    let pendingCount = 0;
    let totalDurationMs = 0;
    let latestCheckpoint: CheckpointData | null = null;
    let latestTimestamp = 0;

    for (const checkpoint of iterations.values()) {
        switch (checkpoint.status) {
            case 'completed':
                completedCount++;
                break;
            case 'failed':
                failedCount++;
                break;
            case 'running':
                runningCount++;
                break;
            case 'pending':
                pendingCount++;
                break;
        }

        if (checkpoint.duration_ms) {
            totalDurationMs += checkpoint.duration_ms;
        }

        const timestamp = new Date(checkpoint.created).getTime();
        if (timestamp > latestTimestamp) {
            latestTimestamp = timestamp;
            latestCheckpoint = checkpoint;
        }
    }

    const totalIterations = iterations.size;
    const avgDurationMs = totalIterations > 0 ? Math.round(totalDurationMs / totalIterations) : 0;

    // Determine aggregated status
    let aggregatedStatus: AggregatedCheckpointData['aggregatedStatus'];
    if (failedCount > 0) {
        aggregatedStatus = 'failed';
    } else if (completedCount === totalIterations && totalIterations > 0) {
        aggregatedStatus = 'completed';
    } else if (runningCount > 0) {
        aggregatedStatus = 'running';
    } else if (completedCount > 0) {
        aggregatedStatus = 'partial';
    } else {
        aggregatedStatus = 'pending';
    }

    return {
        stateName: innerStateName,
        mapNodeId,
        totalIterations,
        completedCount,
        failedCount,
        runningCount,
        pendingCount,
        totalDurationMs,
        avgDurationMs,
        iterations,
        latestCheckpoint,
        hasFailed: failedCount > 0,
        allCompleted: completedCount === totalIterations && totalIterations > 0,
        aggregatedStatus,
    };
}

/**
 * Hook for managing workflow replay state.
 * 
 * Provides functionality to:
 * - Enter/exit replay mode
 * - List and select historical workflow runs
 * - Load checkpoint data for a run
 * - Correlate logs with checkpoints via span_id
 * - Track selected node for inspector drawer
 * - Handle map iteration checkpoints with aggregation
 * - Navigate into child workflow runs
 */
export function useWorkflowReplay(options: UseWorkflowReplayOptions): UseWorkflowReplayReturn {
    const { workflowId, workflowKey, isReplayMode, initialRunId, onNodeSelected } = options;

    // Internal state (excludes isReplayMode which is now controlled by parent)
    const [state, setState] = useState<Omit<ReplayState, 'isReplayMode'>>({
        selectedRunId: null,
        selectedRun: null,
        checkpoints: new Map(),
        logs: [],
        selectedNodeId: null,
        isLoading: false,
        error: null,
        grafanaUrl: null,
    });

    const [availableRuns, setAvailableRuns] = useState<WorkflowRunSummary[]>([]);
    
    // Index for map iteration checkpoints: key is "mapNodeId:innerStateName"
    // value is Map<iterationIndex, CheckpointData>
    const [mapIterationIndex, setMapIterationIndex] = useState<Map<string, Map<number, CheckpointData>>>(new Map());
    
    // Index for parallel branch checkpoints: key is "parallelNodeId:branchIndex:innerStateName"
    // value is CheckpointData (only one checkpoint per branch step)
    const [parallelBranchIndex, setParallelBranchIndex] = useState<Map<string, CheckpointData>>(new Map());

    // Child workflow runs indexed by parent_state_name
    const [childRunsByState, setChildRunsByState] = useState<Map<string, ChildWorkflowRunSummary>>(new Map());

    // Clear replay data (called when exiting replay mode)
    const clearReplayData = useCallback(() => {
        setState({
            selectedRunId: null,
            selectedRun: null,
            checkpoints: new Map(),
            logs: [],
            selectedNodeId: null,
            isLoading: false,
            error: null,
            grafanaUrl: null,
        });
        setAvailableRuns([]);
        setMapIterationIndex(new Map());
        setParallelBranchIndex(new Map());
        setChildRunsByState(new Map());
    }, []);

    // Fetch available runs
    const fetchRuns = useCallback(async () => {
        if (!workflowId && !workflowKey) return;

        setState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            // Prefer FQCN (matches workflow_runs.workflow_name); UUID is a fallback.
            const identifier = workflowKey || workflowId;
            console.log('[Replay] Fetching runs for workflow:', { workflowId, workflowKey, identifier });
            
            const response = await apiGet<ListRunsResponse>(
                `/api/workflows/${encodeURIComponent(identifier)}/runs?per_page=50`
            );
            console.log('[Replay] Fetched runs:', response.data.length);
            setAvailableRuns(response.data);
        } catch (error) {
            console.error('Failed to fetch workflow runs:', error);
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : 'Failed to fetch runs',
            }));
        } finally {
            setState(prev => ({ ...prev, isLoading: false }));
        }
    }, [workflowId, workflowKey]);

    // Refresh runs (public method)
    const refreshRuns = useCallback(async () => {
        await fetchRuns();
    }, [fetchRuns]);

    // Fetch runs when entering replay mode
    useEffect(() => {
        if (isReplayMode && (workflowId || workflowKey)) {
            fetchRuns();
        }
    }, [isReplayMode, workflowId, workflowKey, fetchRuns]);
    
    // Clear data when exiting replay mode
    useEffect(() => {
        if (!isReplayMode) {
            clearReplayData();
        }
    }, [isReplayMode, clearReplayData]);

    // Select a run and load its checkpoints
    const selectRun = useCallback(async (runId: string) => {
        setState(prev => ({ 
            ...prev, 
            isLoading: true, 
            error: null,
            selectedRunId: runId,
        }));

        try {
            // Fetch checkpoints
            const checkpointsResponse = await apiGet<GetCheckpointsResponse>(
                `/api/workflow-runs/${encodeURIComponent(runId)}/checkpoints`
            );

            // Phase 1: Diagnostic logging to verify checkpoint state_name formats
            console.log('[Replay] All checkpoint state_names:', 
                checkpointsResponse.data.map(c => c.state_name));

            // Build checkpoint map by state_name
            const checkpointMap = new Map<string, CheckpointData>();
            // Also build map iteration index
            const iterationIndex = new Map<string, Map<number, CheckpointData>>();
            // Also build parallel branch index
            const branchIndex = new Map<string, CheckpointData>();
            
            for (const checkpoint of checkpointsResponse.data) {
                // Check if this is a map iteration checkpoint
                const mapInfo = parseMapIterationStateName(checkpoint.state_name);
                // Check if this is a parallel branch checkpoint
                const branchInfo = parseParallelBranchStateName(checkpoint.state_name);
                
                if (mapInfo) {
                    // This is a map iteration checkpoint - index it
                    const key = `${mapInfo.mapNodeId}:${mapInfo.innerStateName}`;
                    if (!iterationIndex.has(key)) {
                        iterationIndex.set(key, new Map());
                    }
                    const iterations = iterationIndex.get(key)!;
                    
                    // Use the latest attempt for each iteration
                    const existing = iterations.get(mapInfo.iterationIndex);
                    if (!existing || checkpoint.attempt > existing.attempt) {
                        iterations.set(mapInfo.iterationIndex, checkpoint);
                    }
                    
                    console.log('[Replay] Indexed map iteration checkpoint:', {
                        key,
                        iterationIndex: mapInfo.iterationIndex,
                        stateName: checkpoint.state_name,
                        status: checkpoint.status,
                    });
                } else if (branchInfo) {
                    // This is a parallel branch checkpoint - index it
                    // Key format: "parallelNodeId:branchIndex:innerStateName"
                    const key = `${branchInfo.parallelNodeId}:${branchInfo.branchIndex}:${branchInfo.innerStateName}`;
                    
                    // Use the latest attempt for each branch step
                    const existing = branchIndex.get(key);
                    if (!existing || checkpoint.attempt > existing.attempt) {
                        branchIndex.set(key, checkpoint);
                    }
                    
                    console.log('[Replay] Indexed parallel branch checkpoint:', {
                        key,
                        parallelNodeId: branchInfo.parallelNodeId,
                        branchIndex: branchInfo.branchIndex,
                        innerStateName: branchInfo.innerStateName,
                        stateName: checkpoint.state_name,
                        status: checkpoint.status,
                    });
                } else {
                    // Regular checkpoint - use the latest attempt for each state
                    const existing = checkpointMap.get(checkpoint.state_name);
                    if (!existing || checkpoint.attempt > existing.attempt) {
                        checkpointMap.set(checkpoint.state_name, checkpoint);
                    }
                }
            }

            console.log('[Replay] Checkpoint indexing complete:', {
                regularCheckpoints: checkpointMap.size,
                mapIterationKeys: iterationIndex.size,
                parallelBranchKeys: branchIndex.size,
            });

            // Find the selected run in available runs, or construct from checkpoint response
            // This handles cases where the run isn't in availableRuns due to visibility filtering
            // (e.g., when navigating into a child workflow via initialRunId)
            let selectedRun = availableRuns.find(r => r.id === runId) || null;
            
            if (!selectedRun && checkpointsResponse.run) {
                // Construct a minimal run summary from the checkpoints response
                const runInfo = checkpointsResponse.run;
                selectedRun = {
                    id: runInfo.id,
                    // Cast status to the expected union type - backend may return any valid status
                    status: runInfo.status as WorkflowRunSummary['status'],
                    created: runInfo.created,
                    modified: runInfo.created, // Use created as fallback
                    trace_id: runInfo.trace_id || null,
                    root_span_id: runInfo.root_span_id || null,
                    has_trace: Boolean(runInfo.trace_id),
                    input_summary: null,
                    duration_ms: null,
                    current_step_id: null,
                };
                console.log('[Replay] Constructed run from checkpoint response (not in availableRuns):', selectedRun.id);
            }

            // Fetch logs if trace_id is available
            let logs: LogEntry[] = [];
            let grafanaUrl: string | null = null;

            if (checkpointsResponse.run.trace_id) {
                try {
                    const logsResponse = await apiGet<GetLogsResponse>(
                        `/api/workflow-runs/${encodeURIComponent(runId)}/logs`
                    );
                    logs = logsResponse.data;
                    grafanaUrl = logsResponse.grafana_url || null;
                } catch (logError) {
                    // Logs are optional - don't fail if Loki is unavailable
                    console.warn('Failed to fetch logs:', logError);
                }
            }

            // Fetch child runs for this workflow run
            let childRuns = new Map<string, ChildWorkflowRunSummary>();
            try {
                const childRunsResponse = await apiGet<GetChildRunsResponse>(
                    `/api/workflow-runs/${encodeURIComponent(runId)}/children`
                );
                // Index by parent_state_name for easy lookup
                for (const childRun of childRunsResponse.data) {
                    if (childRun.parent_state_name) {
                        childRuns.set(childRun.parent_state_name, childRun);
                    }
                }
                console.log('[Replay] Child runs indexed:', {
                    count: childRuns.size,
                    states: Array.from(childRuns.keys()),
                });
            } catch (childError) {
                // Child runs are optional
                console.warn('Failed to fetch child runs:', childError);
            }

            // Update all states
            setMapIterationIndex(iterationIndex);
            setParallelBranchIndex(branchIndex);
            setChildRunsByState(childRuns);
            
            // If we constructed a selectedRun that wasn't in availableRuns, add it
            // This ensures the run selector shows the current run even for child workflows
            // that were navigated to via initialRunId
            if (selectedRun && !availableRuns.find(r => r.id === selectedRun.id)) {
                console.log('[Replay] Adding constructed run to availableRuns:', selectedRun.id);
                setAvailableRuns(prev => [selectedRun, ...prev]);
            }
            
            setState(prev => ({
                ...prev,
                isLoading: false,
                selectedRun,
                checkpoints: checkpointMap,
                logs,
                grafanaUrl,
                selectedNodeId: null, // Reset selection when changing runs
            }));
        } catch (error) {
            console.error('Failed to load run data:', error);
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Failed to load run data',
            }));
        }
    }, [availableRuns]);

    // Get aggregated checkpoint data for a node inside a map iterator
    const getMapIterationCheckpoints = useCallback((mapNodeId: string, innerStateName: string): AggregatedCheckpointData | null => {
        const key = `${mapNodeId}:${innerStateName}`;
        const iterations = mapIterationIndex.get(key);
        
        if (!iterations || iterations.size === 0) {
            return null;
        }
        
        return buildAggregatedCheckpoint(mapNodeId, innerStateName, iterations);
    }, [mapIterationIndex]);

    // Get checkpoint for a specific node/state
    // For map iterator nodes, also check the iteration index
    // For parallel branch nodes, check the parallel branch index
    const getCheckpointForNode = useCallback((
        stateName: string, 
        mapNodeId?: string,
        parallelInfo?: { parallelNodeId: string; branchIndex: number }
    ): CheckpointLookupResult => {
        // Clean up the state name
        const cleanStateName = stateName.replace(/^state-/, '');
        
        // 1. Direct lookup FIRST (handles regular + post-parallel steps)
        const directCheckpoint = state.checkpoints.get(cleanStateName) || 
                                 state.checkpoints.get(stateName) || 
                                 null;
        
        if (directCheckpoint) {
            return {
                isMapIteration: false,
                isParallelBranch: false,
                checkpoint: directCheckpoint,
                aggregated: null,
            };
        }
        
        // 2. Parallel branch lookup (ONLY if parallelInfo provided)
        if (parallelInfo) {
            const key = `${parallelInfo.parallelNodeId}:${parallelInfo.branchIndex}:${cleanStateName}`;
            const branchCheckpoint = parallelBranchIndex.get(key);
            if (branchCheckpoint) {
                return {
                    isMapIteration: false,
                    isParallelBranch: true,
                    checkpoint: branchCheckpoint,
                    aggregated: null,
                };
            }
        }
        
        // 3. Map iteration lookup (if mapNodeId is provided)
        if (mapNodeId) {
            const aggregated = getMapIterationCheckpoints(mapNodeId, cleanStateName);
            if (aggregated) {
                return {
                    isMapIteration: true,
                    isParallelBranch: false,
                    checkpoint: aggregated.latestCheckpoint,
                    aggregated,
                };
            }
        }
        
        // 4. Fallback: Try to find by searching all map iteration keys for this state name
        // NOTE: We do NOT do fallback search for parallel branches to avoid false matches
        // with nodes outside the parallel that happen to have the same name
        for (const [key, iterations] of mapIterationIndex.entries()) {
            const [iterMapNodeId, iterInnerState] = key.split(':');
            if (iterInnerState === cleanStateName && iterations.size > 0) {
                const aggregated = buildAggregatedCheckpoint(iterMapNodeId, iterInnerState, iterations);
                return {
                    isMapIteration: true,
                    isParallelBranch: false,
                    checkpoint: aggregated.latestCheckpoint,
                    aggregated,
                };
            }
        }
        
        return {
            isMapIteration: false,
            isParallelBranch: false,
            checkpoint: null,
            aggregated: null,
        };
    }, [state.checkpoints, mapIterationIndex, parallelBranchIndex, getMapIterationCheckpoints]);

    // Select a node to inspect
    const selectNode = useCallback((nodeId: string, mapNodeId?: string) => {
        setState(prev => ({ ...prev, selectedNodeId: nodeId }));

        // Find checkpoint for this node
        const stateName = nodeId.replace(/^state-/, '');
        const result = getCheckpointForNode(stateName, mapNodeId);

        if (onNodeSelected) {
            onNodeSelected(stateName, result.checkpoint, result.aggregated);
        }
    }, [getCheckpointForNode, onNodeSelected]);

    // Get logs for a specific node/state (filtered by span_id)
    const getLogsForNode = useCallback((stateName: string): LogEntry[] => {
        const result = getCheckpointForNode(stateName);
        
        // For map iterations, collect logs from all iteration checkpoints
        if (result.isMapIteration && result.aggregated) {
            const spanIds = new Set<string>();
            for (const checkpoint of result.aggregated.iterations.values()) {
                if (checkpoint.span_id) {
                    spanIds.add(checkpoint.span_id);
                }
            }
            return state.logs.filter(log => log.span_id && spanIds.has(log.span_id));
        }
        
        // For regular checkpoints
        if (!result.checkpoint?.span_id) {
            return [];
        }

        return state.logs.filter(log => log.span_id === result.checkpoint?.span_id);
    }, [state.logs, getCheckpointForNode]);

    // Get child runs for a specific step
    const getChildRunsForStep = useCallback(async (stepId: string): Promise<ChildWorkflowRunSummary[]> => {
        if (!state.selectedRunId) return [];
        
        try {
            const response = await apiGet<GetChildRunsResponse>(
                `/api/workflow-runs/${encodeURIComponent(state.selectedRunId)}/children?step_id=${encodeURIComponent(stepId)}`
            );
            return response.data;
        } catch (error) {
            console.error('Failed to fetch child runs for step:', error);
            return [];
        }
    }, [state.selectedRunId]);

    // Check if a child run exists for a given state name
    const hasChildRun = useCallback((stateName: string): boolean => {
        return childRunsByState.has(stateName);
    }, [childRunsByState]);

    // Track previous initialRunId to detect changes
    const prevInitialRunIdRef = useRef<string | null | undefined>(null);

    // Auto-select the initial run once we're in replay mode and runs are loaded
    // Also handles when initialRunId changes (e.g., switching between child workflows)
    useEffect(() => {
        if (!initialRunId || !isReplayMode || state.isLoading) {
            return;
        }
        
        // Select if no run is selected OR if initialRunId changed
        const shouldSelect = !state.selectedRunId || 
            (prevInitialRunIdRef.current !== initialRunId && prevInitialRunIdRef.current !== null);
        
        if (shouldSelect) {
            console.log('[Replay] Auto-selecting initial run:', initialRunId, {
                previousRunId: prevInitialRunIdRef.current,
                currentSelectedId: state.selectedRunId,
            });
            selectRun(initialRunId);
        }
        
        prevInitialRunIdRef.current = initialRunId;
    }, [initialRunId, isReplayMode, state.selectedRunId, state.isLoading, selectRun]);

    return {
        // State (isReplayMode is passed through from options)
        isReplayMode,
        ...state,
        availableRuns,
        
        // Actions
        selectRun,
        selectNode,
        refreshRuns,
        clearReplayData,
        
        // Helpers
        getCheckpointForNode,
        getLogsForNode,
        getMapIterationCheckpoints,
        getChildRunsForStep,
        hasChildRun,
        childRunsByState,
    };
}

